/**
 * Merkezi Auth Servisi
 *
 * Login response formatı:
 * { jwt: { access_token, refresh_token, expiration_date }, otp_ttl }
 *
 * Tüm token yönetimi bu modülden yapılır.
 * Login yöntemi fark etmez (JWT URL, form login, SSO vb.)
 */
import axios from "axios";
import {
  getApiAuthAccount,
  postApiAuthJwtLogout,
  postApiAuthJwtRefreshToken,
} from "@/api/auth-api";

const TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const TOKEN_EXPIRY_KEY = "token_expiry";
const USER_ID_KEY = "userId";
const API_KEY = "api";

// ─── Token Yönetimi ───────────────────────────────────

export function getAccessToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken() {
  return sessionStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Login API response'undan gelen jwt objesini kaydet.
 * @param {Object} jwtPayload - { access_token, refresh_token, expiration_date }
 */
export function saveTokens(jwtPayload) {
  if (jwtPayload?.access_token) {
    sessionStorage.setItem(TOKEN_KEY, jwtPayload.access_token);
  }
  if (jwtPayload?.refresh_token) {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, jwtPayload.refresh_token);
  }
  if (jwtPayload?.expiration_date) {
    sessionStorage.setItem(TOKEN_EXPIRY_KEY, jwtPayload.expiration_date);
  }
}

/**
 * Yalnızca access_token kaydet (JWT URL ile geldiğinde)
 */
export function setAccessToken(token) {
  if (token) {
    sessionStorage.setItem(TOKEN_KEY, token);
  }
}

/**
 * Oturumu sunucuda kapatır (çıkış / engellenmiş oturumun temizliği). Hata yutulur: yerel çıkış her
 * durumda tamamlanmalı. Refresh token yoksa istek atılmaz.
 */
export async function revokeServerSession() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return;
  try {
    await postApiAuthJwtLogout({ refresh_token: refreshToken });
  } catch (error) {
    console.warn("Oturum sunucuda kapatılamadı (kritik değil):", error);
  }
}

export function clearAuth() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
  sessionStorage.removeItem(USER_ID_KEY);
}

export function isAuthenticated() {
  return !!getAccessToken();
}

export function isTokenExpired() {
  const expiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiry) return false;
  return new Date(expiry) <= new Date();
}

// ─── API Domain Yönetimi ──────────────────────────────

export function getApiDomain() {
  return sessionStorage.getItem(API_KEY) ?? "/";
}

export function setApiDomain(domain) {
  if (domain) {
    sessionStorage.setItem(API_KEY, domain);
  }
}

// ─── Axios Interceptor ───────────────────────────────

let _router = null;
let _store = null;
let _isRefreshing = false;
let _failedQueue = [];

function processQueue(error, token = null) {
  _failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  _failedQueue = [];
}

export function initAuthInterceptors(router, store) {
  _router = router;
  _store = store;

  // Request interceptor — her isteğe güncel token ekle
  axios.interceptors.request.use(
    (config) => {
      const token = getAccessToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor — 401'de önce refresh dene, olmuyorsa logout
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Login endpoint'i 401 dönerse direkt reddet (sonsuz döngü önlemi)
      if (
        error?.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url?.includes("/auth/Jwt/login") &&
        !originalRequest.url?.includes("/auth/Jwt/refresh-token")
      ) {
        if (_isRefreshing) {
          // Zaten refresh yapılıyorsa kuyruğa ekle
          return new Promise((resolve, reject) => {
            _failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axios(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        _isRefreshing = true;

        const refreshToken = getRefreshToken();

        if (refreshToken) {
          try {
            const res = await postApiAuthJwtRefreshToken({
              refresh_token: refreshToken,
            });

            const jwtData = res.data?.jwt || res.data;
            saveTokens(jwtData);

            const newToken = getAccessToken();
            processQueue(null, newToken);

            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            _doLogout();
            return Promise.reject(refreshError);
          } finally {
            _isRefreshing = false;
          }
        } else {
          _isRefreshing = false;
          _doLogout();
        }
      }

      return Promise.reject(error);
    }
  );
}

function _doLogout() {
  if (_store) {
    _store.dispatch("logout");
  }
  if (_router) {
    const currentRoute = _router.currentRoute?.value?.name;
    if (currentRoute !== "login" && currentRoute !== "login-form") {
      _router.push({ name: "login-form" });
    }
  }
}

// ─── Kullanıcı Bilgisi ──────────────────────────────

export async function fetchAndSetUser(store) {
  const token = getAccessToken();
  if (!token) throw new Error("Token bulunamadı");

  store.commit("SET_TOKEN", token);

  const res = await getApiAuthAccount();
  store.commit("SET_USER", res.data);
  sessionStorage.setItem(USER_ID_KEY, res.data.id);

  return res.data;
}

export default {
  getAccessToken,
  getRefreshToken,
  saveTokens,
  setAccessToken,
  clearAuth,
  isAuthenticated,
  isTokenExpired,
  getApiDomain,
  setApiDomain,
  initAuthInterceptors,
  fetchAndSetUser,
};
